import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Star, MessageSquare, Mail, RefreshCw, Download, 
  Check, X, Clock, Lightbulb, AlertTriangle, TrendingUp,
  LogOut, Copy, Trash2, Calendar
} from 'lucide-react';

const AdminDashboardPage = () => {
  const [activeTab, setActiveTab] = useState('notifications');
  const [loading, setLoading] = useState(true);
  const [feedbackFilter, setFeedbackFilter] = useState(null); // For filtering by AI type
  const navigate = useNavigate();
  
  // Data states
  const [notifications, setNotifications] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [feedbackInsights, setFeedbackInsights] = useState({});
  const [ageRequests, setAgeRequests] = useState([]);
  const [ageRequestStats, setAgeRequestStats] = useState({});
  const [copiedAge, setCopiedAge] = useState(null);

  const getAuthHeader = useCallback(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) {
      navigate('/admin');
      return null;
    }
    return { 'Authorization': 'Basic ' + auth };
  }, [navigate]);

  const fetchData = useCallback(async () => {
    const authHeader = getAuthHeader();
    if (!authHeader) return;
    
    setLoading(true);
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    
    try {
      const [notifRes, reviewsRes, testimonialsRes, feedbackRes, insightsRes, ageReqRes, ageStatsRes] = await Promise.all([
        fetch(`${API_URL}/api/notifications`),
        fetch(`${API_URL}/api/admin/reviews`, { headers: authHeader }),
        fetch(`${API_URL}/api/admin/testimonials`, { headers: authHeader }),
        fetch(`${API_URL}/api/admin/feedback`, { headers: authHeader }),
        fetch(`${API_URL}/api/admin/feedback/insights`, { headers: authHeader }),
        fetch(`${API_URL}/api/admin/age-requests`, { headers: authHeader }),
        fetch(`${API_URL}/api/admin/age-requests/stats`, { headers: authHeader }),
      ]);

      if (notifRes.ok) setNotifications(await notifRes.json());
      if (reviewsRes.ok) setReviews(await reviewsRes.json());
      if (testimonialsRes.ok) setTestimonials(await testimonialsRes.json());
      if (ageReqRes.ok) setAgeRequests(await ageReqRes.json());
      if (ageStatsRes.ok) setAgeRequestStats(await ageStatsRes.json());
      if (feedbackRes.ok) setFeedback(await feedbackRes.json());
      if (insightsRes.ok) setFeedbackInsights(await insightsRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuth');
    navigate('/admin');
  };

  const updateReviewStatus = async (reviewId, status) => {
    const authHeader = getAuthHeader();
    if (!authHeader) return;
    
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    await fetch(`${API_URL}/api/admin/reviews/${reviewId}?status=${status}`, {
      method: 'PUT',
      headers: authHeader,
    });
    fetchData();
  };

  const updateTestimonialStatus = async (id, updates) => {
    const authHeader = getAuthHeader();
    if (!authHeader) return;
    
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    await fetch(`${API_URL}/api/admin/testimonials/${id}`, {
      method: 'PUT',
      headers: { ...authHeader, 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });
    fetchData();
  };

  const updateFeedbackStatus = async (id, status) => {
    const authHeader = getAuthHeader();
    if (!authHeader) return;
    
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    await fetch(`${API_URL}/api/admin/feedback/${id}?status=${status}`, {
      method: 'PUT',
      headers: authHeader,
    });
    fetchData();
  };

  const deleteItem = async (type, id) => {
    console.log('Delete called for:', type, id);
    
    const auth = sessionStorage.getItem('adminAuth');
    console.log('Auth from sessionStorage:', auth ? 'exists' : 'missing');
    
    if (!auth) {
      console.error('No auth found, redirecting to login');
      navigate('/admin');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this item?')) {
      console.log('User cancelled delete');
      return;
    }
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const url = `${API_URL}/api/admin/${type}/${id}`;
      console.log('Delete URL:', url);
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': 'Basic ' + auth
        },
      });
      
      console.log('Delete response status:', response.status);
      
      if (!response.ok) {
        const error = await response.text();
        console.error('Delete failed:', response.status, error);
        alert('Failed to delete: ' + error);
        return;
      }
      
      console.log('Delete successful, refreshing data');
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete item: ' + error.message);
    }
  };

  // Group notifications by age (11-12 only for coming soon books)
  const subscribersByAge = {};
  for (let age = 11; age <= 12; age++) {
    subscribersByAge[age] = [];
  }
  notifications.forEach(notif => {
    notif.selected_ages.forEach(age => {
      if (subscribersByAge[age] && !subscribersByAge[age].includes(notif.email)) {
        subscribersByAge[age].push(notif.email);
      }
    });
  });

  // Update age request status
  const updateAgeRequestStatus = async (id, status) => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) return;
    
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    await fetch(`${API_URL}/api/admin/age-requests/${id}?status=${status}`, {
      method: 'PUT',
      headers: { 'Authorization': 'Basic ' + auth },
    });
    fetchData();
  };

  const copyEmails = (emails, age) => {
    navigator.clipboard.writeText(emails.join(', '));
    setCopiedAge(age);
    setTimeout(() => setCopiedAge(null), 2000);
  };

  const exportCSV = () => {
    let csv = 'Email,Ages Subscribed,Date\n';
    notifications.forEach(notif => {
      const date = new Date(notif.created_at).toLocaleDateString();
      csv += `${notif.email},"${notif.selected_ages.join(', ')}",${date}\n`;
    });
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `subscribers-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const allEmails = [...new Set(notifications.map(n => n.email))];
  const pendingReviews = reviews.filter(r => r.status === 'pending');
  const pendingTestimonials = testimonials.filter(t => t.status === 'pending');
  const todoFeedback = feedback.filter(f => f.ai_analysis?.type === 'todo');
  const newAgeRequests = ageRequests.filter(r => r.status === 'new');

  const tabs = [
    { id: 'notifications', label: 'Subscribers', icon: Mail, count: allEmails.length },
    { id: 'age-requests', label: 'Age Requests', icon: Calendar, count: newAgeRequests.length, badge: true },
    { id: 'reviews', label: 'Reviews', icon: Star, count: pendingReviews.length, badge: true },
    { id: 'testimonials', label: 'Testimonials', icon: Users, count: pendingTestimonials.length, badge: true },
    { id: 'feedback', label: 'Feedback', icon: MessageSquare, count: todoFeedback.length, badge: true },
  ];

  const StatusBadge = ({ status }) => {
    const colors = {
      approved: 'bg-green-100 text-green-700',
      pending: 'bg-yellow-100 text-yellow-700',
      rejected: 'bg-red-100 text-red-700',
      new: 'bg-blue-100 text-blue-700',
      reviewed: 'bg-gray-100 text-gray-700',
      implemented: 'bg-green-100 text-green-700',
      dismissed: 'bg-red-100 text-red-700',
      contacted: 'bg-yellow-100 text-yellow-700',
      fulfilled: 'bg-green-100 text-green-700',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100'}`}>
        {status}
      </span>
    );
  };

  const StarRating = ({ rating }) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} size={14} fill={i <= rating ? '#f59e0b' : 'none'} stroke={i <= rating ? '#f59e0b' : '#d1d5db'} />
      ))}
    </div>
  );

  return (
    <div className="pt-20 min-h-screen" style={{ background: 'var(--bg-page)' }}>
      <div className="container py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="heading-hero mb-2">Admin Dashboard</h1>
            <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
              Manage subscribers, reviews, testimonials, and feedback
            </p>
          </div>
          <div className="flex gap-3">
            <button onClick={fetchData} className="btn-secondary flex items-center gap-2" disabled={loading}>
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
            <button onClick={handleLogout} className="btn-secondary flex items-center gap-2 text-red-600">
              <LogOut size={18} />
              Logout
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setFeedbackFilter(null); }}
              className={`flex items-center gap-2 px-5 py-3 rounded-xl font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id 
                  ? 'text-white shadow-lg' 
                  : 'bg-white border border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
              style={activeTab === tab.id ? { background: 'linear-gradient(135deg, #5B7FFF 0%, #A78BFA 100%)' } : {}}
            >
              <tab.icon size={18} />
              {tab.label}
              {tab.badge && tab.count > 0 && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                  activeTab === tab.id ? 'bg-white/30 text-white' : 'bg-red-500 text-white'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        {activeTab === 'notifications' && (
          <div>
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="card">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-blue-200)' }}>
                    <Users size={24} style={{ color: 'var(--brand-primary)' }} />
                  </div>
                  <div>
                    <p className="caption" style={{ color: 'var(--text-secondary)' }}>Total Subscribers</p>
                    <p className="heading-2">{allEmails.length}</p>
                  </div>
                </div>
              </div>
              <div className="card">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-green-200)' }}>
                    <Mail size={24} style={{ color: 'var(--brand-success)' }} />
                  </div>
                  <div>
                    <p className="caption" style={{ color: 'var(--text-secondary)' }}>Total Subscriptions</p>
                    <p className="heading-2">{notifications.reduce((acc, n) => acc + n.selected_ages.length, 0)}</p>
                  </div>
                </div>
              </div>
              <div className="card flex items-center justify-center">
                <button onClick={exportCSV} className="btn-primary flex items-center gap-2">
                  <Download size={18} />
                  Export All CSV
                </button>
              </div>
            </div>

            {/* All Emails */}
            <div className="card mb-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="heading-3">All Subscriber Emails</h2>
                <button onClick={() => copyEmails(allEmails, 'all')} className="btn-secondary flex items-center gap-2 text-sm">
                  {copiedAge === 'all' ? <Check size={16} /> : <Copy size={16} />}
                  {copiedAge === 'all' ? 'Copied!' : 'Copy All'}
                </button>
              </div>
              <div className="p-4 rounded-lg" style={{ background: 'var(--bg-section)', maxHeight: '150px', overflowY: 'auto' }}>
                <p className="body-small" style={{ color: 'var(--text-secondary)', wordBreak: 'break-word' }}>
                  {allEmails.length > 0 ? allEmails.join(', ') : 'No subscribers yet'}
                </p>
              </div>
            </div>

            {/* By Age */}
            <h2 className="heading-2 mb-6">Subscribers by Age</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Object.entries(subscribersByAge).map(([age, emails]) => (
                <div key={age} className="card">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold"
                        style={{ background: emails.length > 0 ? 'var(--gradient-primary)' : 'var(--bg-section)', color: emails.length > 0 ? 'white' : 'var(--text-tertiary)' }}>
                        {age}
                      </div>
                      <div>
                        <p className="body-medium font-semibold">Age {age}</p>
                        <p className="caption" style={{ color: 'var(--text-secondary)' }}>{emails.length} subscriber{emails.length !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                    <button onClick={() => copyEmails(emails, age)} className="p-2 rounded-lg hover:bg-gray-100" disabled={emails.length === 0}>
                      {copiedAge === age ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
                    </button>
                  </div>
                  <div className="p-3 rounded-lg" style={{ background: 'var(--bg-section)', maxHeight: '100px', overflowY: 'auto' }}>
                    {emails.length > 0 ? emails.map((email, idx) => (
                      <p key={idx} className="body-small mb-1" style={{ color: 'var(--text-secondary)' }}>{email}</p>
                    )) : <p className="body-small text-center" style={{ color: 'var(--text-tertiary)' }}>No subscribers</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'age-requests' && (
          <div>
            {/* Age Request Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="card">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-blue-200)' }}>
                    <Calendar size={24} style={{ color: 'var(--brand-primary)' }} />
                  </div>
                  <div>
                    <p className="caption" style={{ color: 'var(--text-secondary)' }}>Total Requests</p>
                    <p className="heading-2">{ageRequestStats.total_requests || 0}</p>
                  </div>
                </div>
              </div>
              {[13, 14, 15, 16, 17, 18].map(age => (
                <div key={age} className="card">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold"
                      style={{ 
                        background: (ageRequestStats.by_age?.[age] || 0) > 0 ? 'linear-gradient(135deg, #5B7FFF 0%, #A78BFA 100%)' : 'var(--bg-section)', 
                        color: (ageRequestStats.by_age?.[age] || 0) > 0 ? 'white' : 'var(--text-tertiary)' 
                      }}>
                      {age}
                    </div>
                    <div>
                      <p className="body-medium font-semibold">Age {age}</p>
                      <p className="caption" style={{ color: 'var(--text-secondary)' }}>
                        {ageRequestStats.by_age?.[age] || 0} request{(ageRequestStats.by_age?.[age] || 0) !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* All Age Requests */}
            <h2 className="heading-2 mb-4">All Age Requests ({ageRequests.length})</h2>
            <div className="space-y-4">
              {ageRequests.map(req => (
                <div key={req.id} className="card">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <p className="body-medium font-semibold">{req.name || req.email}</p>
                        <StatusBadge status={req.status} />
                      </div>
                      <p className="caption" style={{ color: 'var(--text-secondary)' }}>
                        {req.email} • {new Date(req.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <select 
                        value={req.status} 
                        onChange={(e) => updateAgeRequestStatus(req.id, e.target.value)}
                        className="text-sm border rounded px-2 py-1"
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="fulfilled">Fulfilled</option>
                      </select>
                      <button 
                        onClick={() => deleteItem('age-requests', req.id)} 
                        className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-500 border border-red-200"
                        title="Delete request"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {req.requested_ages.sort((a, b) => a - b).map(age => (
                      <span 
                        key={age} 
                        className="px-3 py-1 rounded-full text-sm font-medium text-white"
                        style={{ background: 'linear-gradient(135deg, #5B7FFF 0%, #A78BFA 100%)' }}
                      >
                        Age {age}
                      </span>
                    ))}
                  </div>
                  {req.message && (
                    <p className="body-small p-3 rounded-lg" style={{ background: 'var(--bg-section)', color: 'var(--text-secondary)' }}>
                      {req.message}
                    </p>
                  )}
                </div>
              ))}
              {ageRequests.length === 0 && (
                <div className="card text-center py-12">
                  <Calendar size={48} className="mx-auto mb-4 text-gray-300" />
                  <p className="body-medium" style={{ color: 'var(--text-tertiary)' }}>No age requests yet</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'reviews' && (
          <div>
            {/* Pending Reviews */}
            {pendingReviews.length > 0 && (
              <div className="mb-8">
                <h2 className="heading-2 mb-4 flex items-center gap-2">
                  <AlertTriangle className="text-yellow-500" size={24} />
                  Pending Review ({pendingReviews.length})
                </h2>
                <div className="space-y-4">
                  {pendingReviews.map(review => (
                    <div key={review.id} className="card border-l-4 border-yellow-400">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <div className="flex items-center gap-3 mb-1">
                            <span className="font-semibold">Age {review.age}</span>
                            <StarRating rating={review.rating} />
                            <StatusBadge status={review.status} />
                          </div>
                          <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                            {review.reviewer_name || 'Anonymous'} • {new Date(review.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => updateReviewStatus(review.id, 'approved')} className="p-2 rounded-lg bg-green-100 text-green-600 hover:bg-green-200">
                            <Check size={18} />
                          </button>
                          <button onClick={() => updateReviewStatus(review.id, 'rejected')} className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200">
                            <X size={18} />
                          </button>
                        </div>
                      </div>
                      <h4 className="font-semibold mb-2">{review.title}</h4>
                      <p className="body-small mb-2" style={{ color: 'var(--text-secondary)' }}>{review.content}</p>
                      {review.flagged_reason && (
                        <div className="p-2 rounded bg-yellow-50 text-yellow-700 text-sm">
                          <strong>AI Flag:</strong> {review.flagged_reason}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* All Reviews */}
            <h2 className="heading-2 mb-4">All Reviews ({reviews.length})</h2>
            <div className="space-y-4">
              {reviews.filter(r => r.status !== 'pending').map(review => (
                <div key={review.id} className="card">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <span className="font-semibold">Age {review.age}</span>
                        <StarRating rating={review.rating} />
                        <StatusBadge status={review.status} />
                      </div>
                      <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                        {review.reviewer_name || 'Anonymous'} • {new Date(review.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <button 
                      data-testid={`delete-review-${review.id}`}
                      onClick={() => deleteItem('reviews', review.id)} 
                      className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-500 border border-red-200"
                      title="Delete review"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                  <h4 className="font-semibold mb-2">{review.title}</h4>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>{review.content}</p>
                </div>
              ))}
              {reviews.length === 0 && (
                <div className="card text-center py-12">
                  <Star size={48} className="mx-auto mb-4 text-gray-300" />
                  <p className="body-medium" style={{ color: 'var(--text-tertiary)' }}>No reviews yet</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'testimonials' && (
          <div>
            {/* Pending */}
            {pendingTestimonials.length > 0 && (
              <div className="mb-8">
                <h2 className="heading-2 mb-4 flex items-center gap-2">
                  <Clock className="text-yellow-500" size={24} />
                  Pending Approval ({pendingTestimonials.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {pendingTestimonials.map(t => (
                    <div key={t.id} className="card border-l-4 border-yellow-400">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="font-semibold">{t.name}</p>
                          <p className="caption" style={{ color: 'var(--text-secondary)' }}>{t.role}</p>
                        </div>
                        <StarRating rating={t.rating} />
                      </div>
                      <p className="body-small mb-4" style={{ color: 'var(--text-secondary)' }}>"{t.text}"</p>
                      <div className="flex gap-2">
                        <button onClick={() => updateTestimonialStatus(t.id, { status: 'approved' })} className="btn-primary text-sm flex-1">Approve</button>
                        <button onClick={() => updateTestimonialStatus(t.id, { status: 'rejected' })} className="btn-secondary text-sm">Reject</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Approved */}
            <h2 className="heading-2 mb-4">Approved Testimonials</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {testimonials.filter(t => t.status === 'approved').map(t => (
                <div key={t.id} className="card">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="font-semibold">{t.name}</p>
                      <p className="caption" style={{ color: 'var(--text-secondary)' }}>{t.role}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <StarRating rating={t.rating} />
                      <button 
                        onClick={() => updateTestimonialStatus(t.id, { featured: !t.featured })}
                        className={`p-1.5 rounded ${t.featured ? 'bg-yellow-100 text-yellow-600' : 'hover:bg-gray-100'}`}
                        title={t.featured ? 'Remove from featured' : 'Add to featured'}
                      >
                        <Star size={16} fill={t.featured ? 'currentColor' : 'none'} />
                      </button>
                    </div>
                  </div>
                  <p className="body-small mb-3" style={{ color: 'var(--text-secondary)' }}>"{t.text}"</p>
                  <div className="flex justify-between items-center">
                    {t.featured && <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full">Featured</span>}
                    <button onClick={() => deleteItem('testimonials', t.id)} className="p-1.5 rounded-lg hover:bg-red-100 text-red-500 ml-auto">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
              {testimonials.filter(t => t.status === 'approved').length === 0 && (
                <div className="card text-center py-12 col-span-2">
                  <Users size={48} className="mx-auto mb-4 text-gray-300" />
                  <p className="body-medium" style={{ color: 'var(--text-tertiary)' }}>No approved testimonials yet</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'feedback' && (
          <div>
            {/* AI Insights - Clickable Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <button 
                onClick={() => setFeedbackFilter(null)}
                className={`card text-left transition-all ${feedbackFilter === null ? 'ring-2 ring-gray-400' : 'hover:shadow-md'}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                    <MessageSquare size={20} className="text-gray-600" />
                  </div>
                  <div>
                    <p className="font-semibold">All</p>
                    <p className="caption" style={{ color: 'var(--text-secondary)' }}>View all</p>
                  </div>
                </div>
                <p className="heading-2 mb-2">{feedback.length}</p>
              </button>
              <button 
                onClick={() => setFeedbackFilter('todo')}
                className={`card text-left transition-all ${feedbackFilter === 'todo' ? 'ring-2 ring-red-400' : 'hover:shadow-md'}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                    <AlertTriangle size={20} className="text-red-600" />
                  </div>
                  <div>
                    <p className="font-semibold">To-Dos</p>
                    <p className="caption" style={{ color: 'var(--text-secondary)' }}>Action needed</p>
                  </div>
                </div>
                <p className="heading-2 mb-2">{feedbackInsights.todo?.count || 0}</p>
              </button>
              <button 
                onClick={() => setFeedbackFilter('good_idea')}
                className={`card text-left transition-all ${feedbackFilter === 'good_idea' ? 'ring-2 ring-blue-400' : 'hover:shadow-md'}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <Lightbulb size={20} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold">Good Ideas</p>
                    <p className="caption" style={{ color: 'var(--text-secondary)' }}>Worth considering</p>
                  </div>
                </div>
                <p className="heading-2 mb-2">{feedbackInsights.good_idea?.count || 0}</p>
              </button>
              <button 
                onClick={() => setFeedbackFilter('recurring')}
                className={`card text-left transition-all ${feedbackFilter === 'recurring' ? 'ring-2 ring-purple-400' : 'hover:shadow-md'}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                    <TrendingUp size={20} className="text-purple-600" />
                  </div>
                  <div>
                    <p className="font-semibold">Recurring</p>
                    <p className="caption" style={{ color: 'var(--text-secondary)' }}>Multiple mentions</p>
                  </div>
                </div>
                <p className="heading-2 mb-2">{feedbackInsights.recurring?.count || 0}</p>
              </button>
            </div>

            {/* Feedback List - Filtered */}
            <h2 className="heading-2 mb-4">
              {feedbackFilter ? `${feedbackFilter === 'good_idea' ? 'Good Ideas' : feedbackFilter.charAt(0).toUpperCase() + feedbackFilter.slice(1)} (${feedback.filter(f => f.ai_analysis?.type === feedbackFilter).length})` : `All Feedback (${feedback.length})`}
            </h2>
            <div className="space-y-4">
              {feedback
                .filter(f => feedbackFilter ? f.ai_analysis?.type === feedbackFilter : true)
                .map(f => (
                <div key={f.id} className="card">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        f.ai_analysis?.type === 'todo' ? 'bg-red-100 text-red-700' :
                        f.ai_analysis?.type === 'good_idea' ? 'bg-blue-100 text-blue-700' :
                        f.ai_analysis?.type === 'recurring' ? 'bg-purple-100 text-purple-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {f.ai_analysis?.type || 'general'}
                      </span>
                      <StatusBadge status={f.status} />
                      <span className="caption" style={{ color: 'var(--text-secondary)' }}>
                        {f.category} • {new Date(f.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <select 
                        value={f.status} 
                        onChange={(e) => updateFeedbackStatus(f.id, e.target.value)}
                        className="text-sm border rounded px-2 py-1"
                      >
                        <option value="new">New</option>
                        <option value="reviewed">Reviewed</option>
                        <option value="implemented">Implemented</option>
                        <option value="dismissed">Dismissed</option>
                      </select>
                      <button 
                        data-testid={`delete-feedback-${f.id}`}
                        onClick={() => deleteItem('feedback', f.id)} 
                        className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-500 border border-red-200"
                        title="Delete feedback"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  <p className="body-small mb-2">{f.message}</p>
                  {f.ai_analysis?.summary && (
                    <div className="p-2 rounded bg-gray-50 text-sm">
                      <strong>AI Summary:</strong> {f.ai_analysis.summary}
                      {f.ai_analysis.priority && <span className={`ml-2 px-2 py-0.5 rounded text-xs ${
                        f.ai_analysis.priority === 'high' ? 'bg-red-100 text-red-700' :
                        f.ai_analysis.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }`}>{f.ai_analysis.priority}</span>}
                    </div>
                  )}
                  {(f.email || f.name) && (
                    <p className="caption mt-2" style={{ color: 'var(--text-tertiary)' }}>
                      From: {f.name || 'Anonymous'} {f.email && `(${f.email})`}
                    </p>
                  )}
                </div>
              ))}
              {(feedbackFilter ? feedback.filter(f => f.ai_analysis?.type === feedbackFilter).length === 0 : feedback.length === 0) && (
                <div className="card text-center py-12">
                  <MessageSquare size={48} className="mx-auto mb-4 text-gray-300" />
                  <p className="body-medium" style={{ color: 'var(--text-tertiary)' }}>
                    {feedbackFilter ? `No ${feedbackFilter === 'good_idea' ? 'good ideas' : feedbackFilter} feedback yet` : 'No feedback yet'}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboardPage;
